import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseIcon from '@mui/icons-material/PauseOutlined';
import Replay5Icon from '@mui/icons-material/Replay5Outlined';
import Forward5Icon from '@mui/icons-material/Forward5Outlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VolumeOffIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUpOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import KeyboardIcon from '@mui/icons-material/KeyboardOutlined';
import CropIcon from '@mui/icons-material/CropOutlined';
import { formatMs } from './editStudioModel';
import type { PreviewPlayer } from './usePreviewPlayer';
import type { MarkedRange } from './Timeline';

/**
 * The controls under the stage: play, a frame or five seconds either way, the
 * result's clock (and the source's), looping the marked stretch, the preview's
 * speed, sound, the crop window, fullscreen and the keyboard shortcuts.
 */

interface PreviewTransportProps {
  player: PreviewPlayer;
  muted: boolean;
  onToggleMute: () => void;
  /** One frame, ms. */
  frameMs: number;
  marked: MarkedRange;
  /** Shown when the shape crops the picture. */
  canPositionCrop: boolean;
  positionCrop: boolean;
  onTogglePositionCrop: () => void;
  onFullscreen: () => void;
  onShowShortcuts: () => void;
}

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

const PreviewTransport: React.FC<PreviewTransportProps> = ({ player, muted, onToggleMute, frameMs, marked, canPositionCrop, positionCrop, onTogglePositionCrop, onFullscreen, onShowShortcuts }) => {
  const [rateMenu, setRateMenu] = useState<HTMLElement | null>(null);
  const hasMark = marked.fromMs !== null && marked.toMs !== null && marked.toMs > marked.fromMs;
  const looping = Boolean(player.loop);
  const toggleLoop = () => {
    if (looping) player.setLoop(null);
    else if (hasMark) {
      player.setLoop({ fromMs: marked.fromMs as number, toMs: marked.toMs as number });
      player.play();
    }
  };
  const step = (ms: number) => {
    if (player.playing) player.pause();
    player.seek(player.outputMs + ms);
  };
  return (
    <Box sx={{ bgcolor: '#111827', color: '#F9FAFB', px: 1, py: 0.5, borderRadius: '0 0 10px 10px' }} data-testid="transport">
      <Stack direction="row" alignItems="center" spacing={0.25} useFlexGap flexWrap="wrap">
        <Tooltip title="Back 5 seconds (Shift+←)"><IconButton size="small" onClick={() => player.seek(player.outputMs - 5000)} aria-label="Back 5 seconds" sx={{ color: 'inherit' }}><Replay5Icon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Back a frame (←)"><IconButton size="small" onClick={() => step(-frameMs)} aria-label="Back a frame" sx={{ color: 'inherit' }}><ChevronLeftIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title={player.playing ? 'Pause (Space)' : 'Play (Space)'}>
          <IconButton onClick={player.toggle} aria-label={player.playing ? 'Pause' : 'Play'} data-testid="preview-play" sx={{ color: 'inherit' }}>
            {player.playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Forward a frame (→)"><IconButton size="small" onClick={() => step(frameMs)} aria-label="Forward a frame" sx={{ color: 'inherit' }}><ChevronRightIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Forward 5 seconds (Shift+→)"><IconButton size="small" onClick={() => player.seek(player.outputMs + 5000)} aria-label="Forward 5 seconds" sx={{ color: 'inherit' }}><Forward5Icon fontSize="small" /></IconButton></Tooltip>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', ml: 1 }} data-testid="preview-clock">
          {formatMs(player.outputMs)} / {formatMs(player.durationMs)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#9CA3AF', ml: 1, display: { xs: 'none', sm: 'inline' } }}>
          {player.holding ? 'holding the frame' : `source ${formatMs(player.sourceMs)}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={looping ? 'Stop looping' : hasMark ? 'Loop the marked stretch (L)' : 'Mark a stretch to loop it'}>
          <span>
            <IconButton size="small" onClick={toggleLoop} disabled={!looping && !hasMark} aria-label="Loop the marked stretch" aria-pressed={looping} sx={{ color: looping ? '#FACC15' : 'inherit' }}><RepeatIcon fontSize="small" /></IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Preview speed">
          <Box component="button" type="button" onClick={(event: React.MouseEvent<HTMLButtonElement>) => setRateMenu(event.currentTarget)} aria-label="Preview speed" sx={{ border: '1px solid #374151', bgcolor: 'transparent', color: 'inherit', borderRadius: 1, px: 0.75, py: 0.1, fontSize: 12, cursor: 'pointer' }}>
            {player.rate}×
          </Box>
        </Tooltip>
        <Menu open={Boolean(rateMenu)} anchorEl={rateMenu} onClose={() => setRateMenu(null)}>
          {RATES.map((rate) => (
            <MenuItem key={rate} selected={rate === player.rate} onClick={() => { player.setRate(rate); setRateMenu(null); }}>{rate === 1 ? 'Normal' : `${rate}×`}</MenuItem>
          ))}
        </Menu>
        {canPositionCrop ? (
          <Tooltip title={positionCrop ? 'Back to the result' : 'Position the crop: choose what stays in frame'}>
            <IconButton size="small" onClick={onTogglePositionCrop} aria-label="Position the crop" aria-pressed={positionCrop} sx={{ color: positionCrop ? '#FACC15' : 'inherit' }} data-testid="position-crop"><CropIcon fontSize="small" /></IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title={muted ? 'Unmute (M)' : 'Mute (M)'}>
          <IconButton size="small" onClick={onToggleMute} aria-label={muted ? 'Unmute' : 'Mute'} sx={{ color: 'inherit' }}>{muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}</IconButton>
        </Tooltip>
        <Tooltip title="Fullscreen (F)"><IconButton size="small" onClick={onFullscreen} aria-label="Fullscreen" sx={{ color: 'inherit' }}><FullscreenIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Keyboard shortcuts (?)"><IconButton size="small" onClick={onShowShortcuts} aria-label="Keyboard shortcuts" sx={{ color: 'inherit' }}><KeyboardIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
    </Box>
  );
};

export default PreviewTransport;
